/**
 * Enhanced Contextual Command Palette - Patient-Aware Universal Action Layer
 * 
 * Features:
 * - Bold, colorful UI with clear visibility
 * - Role-based command filtering
 * - Speech-to-text dictation support
 * - Keyboard shortcuts for power users
 * - Voice command integration
 * - Golden Thread patient context sidebar
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Search, FileText, Activity, Users, DollarSign, Calendar,
  Clipboard, Heart, Brain, Pill, AlertTriangle, CheckCircle,
  Phone, MessageSquare, Car, BarChart3, Settings, Clock,
  User, Stethoscope, ChevronRight, Zap, History, Mic, MicOff,
  Keyboard, Volume2, Home, Shield, Building2, FileCheck,
  Bell, PieChart, Target, Award, Sparkles
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Category colors for better visibility
const CATEGORY_COLORS = {
  clinical: { bg: "bg-blue-500", text: "text-blue-50", border: "border-blue-400", icon: "text-blue-400" },
  administrative: { bg: "bg-purple-500", text: "text-purple-50", border: "border-purple-400", icon: "text-purple-400" },
  navigation: { bg: "bg-emerald-500", text: "text-emerald-50", border: "border-emerald-400", icon: "text-emerald-400" },
  compliance: { bg: "bg-amber-500", text: "text-amber-50", border: "border-amber-400", icon: "text-amber-400" },
  communication: { bg: "bg-cyan-500", text: "text-cyan-50", border: "border-cyan-400", icon: "text-cyan-400" },
  general: { bg: "bg-slate-500", text: "text-slate-50", border: "border-slate-400", icon: "text-slate-400" }
};

// Icon mapping
const ICON_MAP = {
  "file-text": FileText, "activity": Activity, "users": Users,
  "dollar-sign": DollarSign, "calendar": Calendar, "clipboard": Clipboard,
  "heart": Heart, "brain": Brain, "pill": Pill, "alert-triangle": AlertTriangle,
  "check-circle": CheckCircle, "phone": Phone, "message-square": MessageSquare,
  "car": Car, "bar-chart-3": BarChart3, "settings": Settings, "clock": Clock,
  "stethoscope": Stethoscope, "zap": Zap, "history": History, "mic": Mic,
  "keyboard": Keyboard, "home": Home, "shield": Shield, "building-2": Building2,
  "file-check": FileCheck, "bell": Bell, "pie-chart": PieChart, "target": Target,
  "award": Award, "sparkles": Sparkles
};

// Keyboard shortcuts
const KEYBOARD_SHORTCUTS = [
  { key: "Ctrl+Shift+V", action: "new_visit_note", label: "Quick Visit Note" },
  { key: "Ctrl+Shift+C", action: "phone_log", label: "Quick Call Log" },
  { key: "Ctrl+Shift+M", action: "mar", label: "MAR" },
  { key: "Ctrl+Shift+H", action: "hope_assessment", label: "HOPE Assessment" },
  { key: "Ctrl+Shift+I", action: "idg_review", label: "IDG Review" },
  { key: "Ctrl+Shift+P", action: "pain_management", label: "Pain Management" },
  { key: "Ctrl+Shift+R", action: "comprehensive_reports", label: "Reports" },
  { key: "Ctrl+Shift+S", action: "schedule_visit", label: "Schedule Visit" }
];

export default function ContextualCommandPalette({
  open,
  onOpenChange,
  patientContext = null,
  viewContext = "general",
  userRole = "staff",
  onAction,
  onNavigate
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPatientPanel, setShowPatientPanel] = useState(true);
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  // Speech-to-text state
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        processVoiceCommand(transcript);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        toast.error("Voice recognition error. Please try again.");
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  // Voice command processor - comprehensive command mapping
  const processVoiceCommand = useCallback((transcript) => {
    const lowerTranscript = transcript.toLowerCase().trim();
    
    // Navigation commands
    const navigationCommands = {
      "report": "/comprehensive-reports",
      "reports": "/comprehensive-reports",
      "comprehensive report": "/comprehensive-reports",
      "dashboard": "/admin",
      "admin dashboard": "/admin",
      "staff portal": "/staff-portal",
      "hospice os": "/hos",
      "hos": "/hos",
      "workspace": "/hos",
      "command center": "/command",
      "calendar": "/calendar",
      "schedule": "/calendar",
      "medications": "/medications",
      "meds": "/medications",
      "mar": "/medications",
      "patient portal": "/patient-portal",
      "family portal": "/family-peace-of-mind",
      "idg": "/idg-summary",
      "idg summary": "/idg-summary",
      "idg meeting": "/idg-meeting",
      "workforce": "/workforce",
      "staff performance": "/staff-performance",
      "analytics": "/analytics",
      "quality": "/quality-compliance",
      "compliance": "/ehr-compliance",
      "hope": "/hope-dashboard",
      "hope assessment": "/hope-dashboard",
      "referral": "/referral-portal",
      "referrals": "/referral-portal",
      "billing": "/billing",
      "home": "/",
    };
    
    // Overlay/action commands
    const actionCommands = {
      "new visit note": { type: "overlay", id: "new_visit_note" },
      "visit note": { type: "overlay", id: "new_visit_note" },
      "new note": { type: "overlay", id: "new_visit_note" },
      "start note": { type: "overlay", id: "new_visit_note" },
      "document": { type: "overlay", id: "new_visit_note" },
      "documentation": { type: "overlay", id: "new_visit_note" },
      "phone log": { type: "overlay", id: "phone_log" },
      "call log": { type: "overlay", id: "phone_log" },
      "log call": { type: "overlay", id: "phone_log" },
      "schedule visit": { type: "overlay", id: "schedule_visit" },
      "new visit": { type: "overlay", id: "schedule_visit" },
      "book visit": { type: "overlay", id: "schedule_visit" },
      "pain management": { type: "overlay", id: "pain_management" },
      "pain": { type: "overlay", id: "pain_management" },
      "wound care": { type: "overlay", id: "wound_care" },
      "wound": { type: "overlay", id: "wound_care" },
      "care plan": { type: "overlay", id: "care_plan" },
      "idg review": { type: "overlay", id: "idg_review" },
      "idg dictation": { type: "overlay", id: "idg_dictation" },
      "smart documentation": { type: "overlay", id: "smart_documentation" },
      "smart docs": { type: "overlay", id: "smart_documentation" },
      "ai documentation": { type: "overlay", id: "smart_documentation" },
      "vital signs": { type: "overlay", id: "vital_signs" },
      "vitals": { type: "overlay", id: "vital_signs" },
      "symptoms": { type: "overlay", id: "symptom_assessment" },
      "symptom check": { type: "overlay", id: "symptom_assessment" },
      "incident report": { type: "overlay", id: "incident_report" },
      "incident": { type: "overlay", id: "incident_report" },
      "family message": { type: "overlay", id: "family_message" },
      "message family": { type: "overlay", id: "family_message" },
      "consent form": { type: "overlay", id: "consent_forms" },
      "consent": { type: "overlay", id: "consent_forms" },
      "dme order": { type: "overlay", id: "dme_supplies" },
      "supplies": { type: "overlay", id: "dme_supplies" },
      "equipment": { type: "overlay", id: "dme_supplies" },
      "physician order": { type: "overlay", id: "physician_orders" },
      "order": { type: "overlay", id: "physician_orders" },
      "recertification": { type: "overlay", id: "recertification" },
      "cti": { type: "overlay", id: "recertification" },
      "export report": { type: "overlay", id: "export_report" },
      "export": { type: "overlay", id: "export_report" },
      "discharge": { type: "overlay", id: "discharge" },
      "intake": { type: "overlay", id: "intake" },
      "new patient": { type: "overlay", id: "intake" },
      "admit": { type: "overlay", id: "intake" },
    };
    
    // Check for navigation commands (triggered by "open", "go to", "navigate", "show")
    const navTriggers = ["open", "go to", "navigate", "show", "take me to", "bring up"];
    const hasNavTrigger = navTriggers.some(trigger => lowerTranscript.includes(trigger));
    
    if (hasNavTrigger) {
      for (const [keyword, path] of Object.entries(navigationCommands)) {
        if (lowerTranscript.includes(keyword)) {
          navigate(path);
          onOpenChange(false);
          toast.success(`Opening ${keyword.charAt(0).toUpperCase() + keyword.slice(1)}...`);
          return;
        }
      }
    }
    
    // Check for action commands (triggered by "start", "new", "create", or direct command)
    const actionTriggers = ["start", "new", "create", "begin", "launch", "open"];
    const hasActionTrigger = actionTriggers.some(trigger => lowerTranscript.includes(trigger));
    
    // Try to match action commands
    for (const [keyword, action] of Object.entries(actionCommands)) {
      if (lowerTranscript.includes(keyword)) {
        if (onAction) {
          onAction(action);
        }
        onOpenChange(false);
        toast.success(`Opening ${keyword.charAt(0).toUpperCase() + keyword.slice(1)}...`);
        return;
      }
    }
    
    // Check for navigation without trigger (direct page names)
    if (!hasNavTrigger && !hasActionTrigger) {
      for (const [keyword, path] of Object.entries(navigationCommands)) {
        if (lowerTranscript.includes(keyword)) {
          navigate(path);
          onOpenChange(false);
          toast.success(`Opening ${keyword.charAt(0).toUpperCase() + keyword.slice(1)}...`);
          return;
        }
      }
    }
    
    // Help command
    if (lowerTranscript.includes("help") || lowerTranscript.includes("what can you do") || lowerTranscript.includes("commands")) {
      toast.info("Try saying: 'Open reports', 'New visit note', 'Schedule visit', 'Go to dashboard', 'IDG review'");
      return;
    }
    
    // Otherwise, use as search query
    toast.info(`Searching for: "${transcript}"`);
  }, [navigate, onOpenChange, onAction]);

  // Toggle speech recognition
  const toggleSpeechRecognition = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
      toast.info("Listening... Speak your command");
    }
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("hospice_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch commands with context
  const fetchCommands = useCallback(async (searchQuery = "") => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/hos/command-palette/contextual`,
        {
          query: searchQuery,
          patient_id: patientContext?.patient_id,
          view_context: viewContext,
          user_role: userRole
        },
        { headers: getAuthHeaders() }
      );
      setCommands(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch commands:", err);
      setCommands([]);
    } finally {
      setLoading(false);
    }
  }, [patientContext, viewContext, userRole, getAuthHeaders]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (open) {
        fetchCommands(query);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [query, open, fetchCommands]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      // Check for shortcuts
      for (const shortcut of KEYBOARD_SHORTCUTS) {
        const keys = shortcut.key.toLowerCase().split("+");
        const ctrlMatch = keys.includes("ctrl") === (e.ctrlKey || e.metaKey);
        const shiftMatch = keys.includes("shift") === e.shiftKey;
        const keyMatch = keys[keys.length - 1] === e.key.toLowerCase();
        
        if (ctrlMatch && shiftMatch && keyMatch) {
          e.preventDefault();
          handleSelect({ action: `overlay:${shortcut.action}`, name: shortcut.label });
          return;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // Handle command execution
  const handleSelect = async (command) => {
    const action = command.action;
    
    if (action?.startsWith("navigate:")) {
      const path = action.replace("navigate:", "");
      if (onNavigate) {
        onNavigate(path);
      } else {
        navigate(path);
      }
      onOpenChange(false);
      toast.success(`Navigating to ${command.name}`);
    } else if (action?.startsWith("overlay:")) {
      const overlayId = action.replace("overlay:", "");
      if (onAction) {
        onAction({ type: "overlay", id: overlayId, command });
      }
      onOpenChange(false);
    } else {
      if (onAction) {
        onAction({ type: "action", command, patientContext });
      }
      onOpenChange(false);
    }
  };

  // Group commands by category
  const groupedCommands = commands.reduce((acc, cmd) => {
    const category = cmd.category || "general";
    if (!acc[category]) acc[category] = [];
    acc[category].push(cmd);
    return acc;
  }, {});

  const categoryLabels = {
    clinical: "🏥 Clinical Documentation",
    administrative: "📋 Administrative",
    navigation: "🧭 Navigate To",
    compliance: "✅ Compliance & QAPI",
    communication: "💬 Communication",
    general: "⚡ General"
  };

  const getCommandIcon = (iconName) => {
    return ICON_MAP[iconName] || FileText;
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <div className="flex max-h-[600px]">
        {/* Main Command Panel */}
        <div className={`flex-1 bg-white dark:bg-slate-900 ${patientContext && showPatientPanel ? "border-r" : ""}`}>
          {/* Header with Voice & Shortcuts */}
          <div className="flex items-center gap-2 border-b px-3 py-2 bg-gradient-to-r from-blue-50 to-white dark:from-slate-800 dark:to-slate-900">
            <Search className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <input
              className="flex-1 h-10 bg-transparent text-base font-medium outline-none placeholder:text-slate-500 dark:placeholder:text-slate-400 text-slate-900 dark:text-white"
              placeholder={patientContext 
                ? `🎯 Commands for ${patientContext.patient_name || "patient"}...`
                : "🔍 Search commands or say a voice command..."
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            
            {/* Voice Command Button */}
            {speechSupported && (
              <Button
                variant={isListening ? "destructive" : "outline"}
                size="sm"
                onClick={toggleSpeechRecognition}
                className="gap-1"
              >
                {isListening ? (
                  <>
                    <MicOff className="w-4 h-4" />
                    <span className="animate-pulse">Listening...</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4" />
                    Voice
                  </>
                )}
              </Button>
            )}
            
            {/* Shortcuts Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowShortcuts(!showShortcuts)}
              className="gap-1"
            >
              <Keyboard className="w-4 h-4" />
              Shortcuts
            </Button>
            
            {patientContext && (
              <Badge className="bg-primary text-primary-foreground font-semibold">
                <User className="w-3 h-3 mr-1" />
                Patient Active
              </Badge>
            )}
          </div>

          {/* Keyboard Shortcuts Panel */}
          {showShortcuts && (
            <div className="border-b bg-slate-50 dark:bg-slate-900 p-3">
              <h4 className="text-xs font-bold uppercase text-muted-foreground mb-2">
                ⌨️ Quick Shortcuts (use from anywhere)
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {KEYBOARD_SHORTCUTS.map((shortcut) => (
                  <div 
                    key={shortcut.key}
                    className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border text-sm"
                  >
                    <span className="font-medium">{shortcut.label}</span>
                    <kbd className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs font-mono">
                      {shortcut.key}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          )}

          <CommandList className="max-h-[400px] bg-slate-50 dark:bg-slate-950">
            {loading && (
              <div className="py-6 text-center">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading commands...</p>
              </div>
            )}

            {!loading && commands.length === 0 && (
              <CommandEmpty>
                <div className="py-8 text-center">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-primary/50" />
                  <p className="text-lg font-semibold">No commands found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Try searching for &quot;visit&quot;, &quot;report&quot;, &quot;schedule&quot;, or &quot;billing&quot;
                  </p>
                  {speechSupported && (
                    <p className="text-xs text-muted-foreground mt-3">
                      💡 Or click <strong>Voice</strong> and say &quot;Open reports&quot;
                    </p>
                  )}
                </div>
              </CommandEmpty>
            )}

            {Object.entries(groupedCommands).map(([category, cmds]) => {
              const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.general;
              return (
                <CommandGroup 
                  key={category} 
                  heading={
                    <div className={`flex items-center gap-2 py-1 px-2 rounded ${colors.bg} ${colors.text} font-bold text-sm`}>
                      {categoryLabels[category] || category}
                      <Badge variant="secondary" className="text-xs">{cmds.length}</Badge>
                    </div>
                  }
                  className="py-2"
                >
                  {cmds.map((cmd) => {
                    const Icon = getCommandIcon(cmd.icon);
                    return (
                      <CommandItem
                        key={cmd.id}
                        value={cmd.name}
                        onSelect={() => handleSelect(cmd)}
                        className={`cursor-pointer my-1 mx-2 rounded-lg border-2 ${colors.border} hover:${colors.bg} hover:${colors.text} transition-all duration-150 bg-white dark:bg-slate-800`}
                      >
                        <div className={`p-2 rounded-lg ${colors.bg} mr-3`}>
                          <Icon className={`h-5 w-5 text-white`} />
                        </div>
                        <div className="flex-1">
                          <span className="font-bold text-base text-slate-900 dark:text-white">{cmd.name}</span>
                          {cmd.description && (
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-1">
                              {cmd.description}
                            </p>
                          )}
                        </div>
                        {cmd.shortcut && (
                          <kbd className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs font-mono text-slate-700 dark:text-slate-200">
                            {cmd.shortcut}
                          </kbd>
                        )}
                        {cmd.action?.startsWith("navigate:") && (
                          <ChevronRight className="w-5 h-5 text-slate-500" />
                        )}}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}

            {/* Footer with hints */}
            <div className="p-3 border-t bg-slate-50 dark:bg-slate-900">
              <div className="flex items-center justify-between text-xs">
                <div className="flex gap-4 text-muted-foreground">
                  <span><kbd className="px-1 bg-slate-200 dark:bg-slate-700 rounded">↑↓</kbd> Navigate</span>
                  <span><kbd className="px-1 bg-slate-200 dark:bg-slate-700 rounded">↵</kbd> Select</span>
                  <span><kbd className="px-1 bg-slate-200 dark:bg-slate-700 rounded">Esc</kbd> Close</span>
                </div>
                {patientContext && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-xs"
                    onClick={() => setShowPatientPanel(!showPatientPanel)}
                  >
                    {showPatientPanel ? "Hide" : "Show"} Patient Context
                  </Button>
                )}
              </div>
            </div>
          </CommandList>
        </div>

        {/* Patient Context Sidebar - Golden Thread Reference */}
        {patientContext && showPatientPanel && (
          <div className="w-80 bg-gradient-to-b from-blue-50 to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <ScrollArea className="h-[560px]">
              <div className="p-4 space-y-4">
                {/* Patient Header */}
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm border-l-4 border-primary">
                  <div className="flex items-center justify-between mb-1">
                    <Badge className="bg-primary text-xs font-bold">GOLDEN THREAD</Badge>
                  </div>
                  <p className="text-xl font-bold text-primary">
                    {patientContext.patient_name || "Unknown Patient"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    MRN: <span className="font-mono font-bold">{patientContext.mrn || "N/A"}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    DOB: {patientContext.dob || "N/A"}
                  </p>
                </div>

                {/* Primary Diagnosis */}
                {patientContext.diagnoses && (
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                    <h5 className="text-xs font-bold uppercase text-amber-600 mb-2">
                      📋 PRIMARY DIAGNOSIS
                    </h5>
                    <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200">
                      <p className="font-bold text-amber-800 dark:text-amber-200">
                        {patientContext.diagnoses.primary?.code}
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        {patientContext.diagnoses.primary?.description}
                      </p>
                    </div>
                  </div>
                )}

                {/* Intake Reference */}
                {patientContext.intake_data && (
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                    <h5 className="text-xs font-bold uppercase text-blue-600 mb-2">
                      📝 INTAKE ANCHOR
                    </h5>
                    <p className="text-sm font-medium mb-1">Primary Complaint:</p>
                    <p className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                      {patientContext.intake_data.primary_complaint || "N/A"}
                    </p>
                    <p className="text-xs mt-2 text-muted-foreground">
                      <strong>Source:</strong> {patientContext.intake_data.referral_source || "N/A"}
                    </p>
                  </div>
                )}

                {/* Assigned Team */}
                {patientContext.admission_data?.assigned_team && (
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                    <h5 className="text-xs font-bold uppercase text-emerald-600 mb-2">
                      👥 CARE TEAM
                    </h5>
                    <div className="space-y-1">
                      {patientContext.admission_data.assigned_team.slice(0, 4).map((member, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm p-1 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                          <User className="w-4 h-4 text-emerald-600" />
                          <span className="font-medium">{member.name}</span>
                          <Badge variant="outline" className="text-xs ml-auto">{member.role}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Activity */}
                {patientContext.last_contacts?.length > 0 && (
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                    <h5 className="text-xs font-bold uppercase text-purple-600 mb-2">
                      🕐 RECENT ACTIVITY
                    </h5>
                    <div className="space-y-2">
                      {patientContext.last_contacts.slice(0, 2).map((contact, idx) => (
                        <div key={idx} className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            {contact.type === "visit" ? (
                              <Stethoscope className="w-4 h-4 text-purple-600" />
                            ) : (
                              <Phone className="w-4 h-4 text-purple-600" />
                            )}
                            <span className="font-medium capitalize">{contact.type}</span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {new Date(contact.date).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {contact.summary || "No summary"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Stats */}
                {patientContext.admission_data && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm text-center">
                      <p className="text-2xl font-bold text-primary">
                        {patientContext.admission_data.level_of_care || "RHC"}
                      </p>
                      <p className="text-xs text-muted-foreground">Level of Care</p>
                    </div>
                    <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm text-center">
                      <p className="text-2xl font-bold text-primary">
                        {patientContext.admission_data.benefit_period || "1st"}
                      </p>
                      <p className="text-xs text-muted-foreground">Benefit Period</p>
                    </div>
                  </div>
                )}

                {/* Dictation Hint */}
                <div className="p-3 bg-gradient-to-r from-primary/10 to-indigo-500/10 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Mic className="w-4 h-4" />
                    Speech-to-Text Ready
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use voice dictation in all documentation forms. Click the mic icon when documenting.
                  </p>
                </div>
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </CommandDialog>
  );
}
